import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Grid, CircularProgress, Alert, LinearProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Warning as WarningIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DigitalCertificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    certificate_file: null,
    certificate_password: ''
  });

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar certificados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.certificate_file || !uploadData.certificate_password) {
      toast.error('Selecione o arquivo e informe a senha');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('name', uploadData.name || 'Certificado Principal');
      formData.append('certificate_file', uploadData.certificate_file);
      formData.append('certificate_password', uploadData.certificate_password);

      await axios.post(`${API}/api/certificates`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Certificado enviado com sucesso!');
      setOpenUpload(false);
      setUploadData({ name: '', certificate_file: null, certificate_password: '' });
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao enviar certificado');
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (cert) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/certificates/${cert.id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(cert.is_active ? 'Certificado desativado' : 'Certificado ativado');
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDelete = async (cert) => {
    if (!window.confirm('Tem certeza que deseja excluir este certificado?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/certificates/${cert.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificado excluído');
      fetchCertificates();
    } catch (error) {
      toast.error('Erro ao excluir certificado');
    }
  };

  const getExpirationStatus = (cert) => {
    if (cert.is_expired) {
      return <Chip icon={<InactiveIcon />} label="Expirado" color="error" size="small" />;
    }
    if (cert.is_expiring_soon) {
      return <Chip icon={<WarningIcon />} label={`Expira em ${cert.days_until_expiration} dias`} color="warning" size="small" />;
    }
    return <Chip icon={<ActiveIcon />} label={`Válido (${cert.days_until_expiration} dias)`} color="success" size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Certificados Digitais
        </Typography>
        <Button
          variant="contained"
          startIcon={<UploadIcon />}
          onClick={() => setOpenUpload(true)}
        >
          Enviar Certificado
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        O certificado digital A1 (.pfx ou .p12) é necessário para emissão de NF-e, NFC-e e MDF-e.
        Apenas um certificado pode estar ativo por vez.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Validade</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ativo</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : certificates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum certificado cadastrado
                </TableCell>
              </TableRow>
            ) : (
              certificates.map((cert) => (
                <TableRow key={cert.id}>
                  <TableCell>{cert.name}</TableCell>
                  <TableCell>{cert.cpf_cnpj}</TableCell>
                  <TableCell>
                    {new Date(cert.valid_from).toLocaleDateString('pt-BR')} até{' '}
                    {new Date(cert.valid_until).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>{getExpirationStatus(cert)}</TableCell>
                  <TableCell>
                    {cert.is_active ? (
                      <Chip icon={<ActiveIcon />} label="Ativo" color="success" size="small" />
                    ) : (
                      <Chip icon={<InactiveIcon />} label="Inativo" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleToggle(cert)}
                      disabled={cert.is_expired}
                    >
                      {cert.is_active ? 'Desativar' : 'Ativar'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(cert)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Upload */}
      <Dialog open={openUpload} onClose={() => setOpenUpload(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Certificado Digital</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome do Certificado"
                placeholder="Ex: Certificado Principal"
                value={uploadData.name}
                onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadIcon />}
              >
                {uploadData.certificate_file?.name || 'Selecionar arquivo .pfx ou .p12'}
                <input
                  type="file"
                  hidden
                  accept=".pfx,.p12"
                  onChange={(e) => setUploadData({ ...uploadData, certificate_file: e.target.files[0] })}
                />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Senha do Certificado"
                value={uploadData.certificate_password}
                onChange={(e) => setUploadData({ ...uploadData, certificate_password: e.target.value })}
              />
            </Grid>
            {uploading && (
              <Grid item xs={12}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpload(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
          >
            {uploading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
